import type { Locale } from './i18n';

/**
 * Formatting helpers. Everything here is locale-aware, and every number that can
 * change in place is rendered with tabular figures by the caller's CSS.
 */

/**
 * Arabic is written with Western digits, "US$ 100,000.00" and "09/10/2026", as in
 * English (operator, 2026-10-09: "update numbers in arabic version in all screens
 * make like english"). `nu-latn` keeps Arabic's words and order — month names,
 * "منذ 5 دقائق" — and changes only the digits and their separators.
 */
const LOCALE_TAG: Record<Locale, string> = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' };

export function formatDate(value: string | Date | null | undefined, locale: Locale = 'en'): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE_TAG[locale], { dateStyle: 'medium' }).format(d);
}

export function formatDateTime(value: string | Date | null | undefined, locale: Locale = 'en'): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE_TAG[locale], { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

export function formatTime(value: string | Date | null | undefined, locale: Locale = 'en'): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(LOCALE_TAG[locale], { timeStyle: 'medium' }).format(d);
}

export function formatMoney(
  value: number | string | null | undefined,
  currency = 'EGP',
  locale: Locale = 'en',
): string {
  const n = value === null || value === undefined ? 0 : Number(value);
  const formatted = new Intl.NumberFormat(LOCALE_TAG[locale], {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(n);
  return locale === 'ar' ? isolateLatinSymbol(formatted) : formatted;
}

/**
 * Arabic Intl writes "100.00 US$". Right to left, the bidi algorithm sends the
 * "$" of a Latin symbol to the far side of its letters, and the screen says
 * "$US". A left-to-right isolate keeps "US$", "CA$" or "CHF" whole. The API's
 * invoice and receipt PDFs do the same, so paper and screen agree.
 */
function isolateLatinSymbol(formatted: string): string {
  return formatted
    .split(/([\s\u00A0]+)/)
    .map((part) =>
      /[A-Za-z]/.test(part) && !/[\d٠-٩]/.test(part)
        ? `\u2066${part.replace(/[\u061C\u200E\u200F]/g, '')}\u2069`
        : part,
    )
    .join('');
}

export function formatNumber(value: number | string | null | undefined, locale: Locale = 'en'): string {
  const n = value === null || value === undefined ? 0 : Number(value);
  return new Intl.NumberFormat(LOCALE_TAG[locale]).format(n);
}

/** `1.4 MB` — a file's size, as the admin shows it. */
export function formatBytes(bytes: number | null | undefined, locale: Locale = 'en'): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  const units = locale === 'ar' ? ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت'] : ['B', 'KB', 'MB', 'GB'];
  let value = Math.max(0, bytes);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  const digits = unit === 0 || value >= 10 ? 0 : 1;
  return `${new Intl.NumberFormat(LOCALE_TAG[locale], { maximumFractionDigits: digits }).format(value)} ${units[unit]}`;
}

export function formatPercent(value: number | null | undefined, locale: Locale = 'en'): string {
  const n = value ?? 0;
  return new Intl.NumberFormat(LOCALE_TAG[locale], { maximumFractionDigits: 1 }).format(n) + '%';
}

/**
 * §37.3 — the SLA countdown. The server supplies the seconds remaining; the client
 * only ticks it down for display, so a clock skew on the workstation never changes
 * whether an SLA was met.
 */
export function formatCountdown(seconds: number): string {
  const overdue = seconds < 0;
  const abs = Math.abs(Math.round(seconds));
  const m = Math.floor(abs / 60);
  const s = abs % 60;

  if (m >= 60) {
    const h = Math.floor(m / 60);
    return `${overdue ? '−' : ''}${h}h ${m % 60}m`;
  }
  return `${overdue ? '−' : ''}${m}:${String(s).padStart(2, '0')}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const abs = Math.round(Math.abs(seconds));
  if (abs < 60) return `${abs}s`;
  if (abs < 3600) return `${Math.floor(abs / 60)}m ${abs % 60}s`;
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  return `${h}h ${m}m`;
}

export function relativeTime(value: string | Date | null | undefined, locale: Locale = 'en'): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diffSeconds = Math.round((d.getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(LOCALE_TAG[locale], { numeric: 'auto' });

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
  ];
  for (const [unit, secondsInUnit] of units) {
    if (Math.abs(diffSeconds) >= secondsInUnit || unit === 'second') {
      return rtf.format(Math.round(diffSeconds / secondsInUnit), unit);
    }
  }
  return '—';
}

/** Weekday names indexed the way JavaScript indexes them: 0 = Sunday … 6 = Saturday. */
export const DAY_NAMES: Record<Locale, string[]> = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  ar: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
};

export const MONTH_NAMES: Record<Locale, string[]> = {
  en: ['January','February','March','April','May','June','July','August','September','October','November','December'],
  ar: ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'],
};

export function isoDate(date: Date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

/**
 * Today on this computer's calendar, `YYYY-MM-DD`. `isoDate()` is UTC's date,
 * which in Cairo is still yesterday for the first hours after midnight — the
 * hours a contract's day-1 invoice is raised in.
 */
export function localIsoDate(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function addDays(dateString: string, days: number): string {
  const d = new Date(`${dateString}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
